import { useSpring } from 'framer-motion'
import { FC, useEffect, useRef, useState, useCallback, useMemo } from 'react'

import { useDataContext } from 'context/data-context'
import { usePlayerContext } from 'context/player-context'
import { WelcomeModal } from './welcome-modal'
import { Player } from './player'
import { playStart } from 'utils'
import { useLocation } from 'react-router-dom'

type Props = {
  year: string
  isHomepage: boolean
  canPlaySound: boolean
}

type AudioControls = {
  audioCtx: AudioContext | null
  gainNode: GainNode | null
}

const radioControls: AudioControls = {
  audioCtx: null,
  gainNode: null,
}
const noiseControls: AudioControls = {
  audioCtx: null,
  gainNode: null,
}

const NOISE_MULTIPLIER = 0.5

let timeoutId: ReturnType<typeof setTimeout>

export const Audio: FC<Props> = ({ year, isHomepage, canPlaySound }) => {
  const { addNotification, articles } = useDataContext()
  const {
    permissionsGranted,
    grantPermissions,
    isMuted,
    toggleMuted,
    masterVolume,
  } = usePlayerContext()
  const location = useLocation()
  const audioUrl = articles[year]?.audioUrl
  const noiseRef = useRef<HTMLAudioElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlayingNoise, setPlayingNoise] = useState(false)
  const noiseVolume = useSpring(0, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  })
  const radioVolume = useSpring(0, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  })

  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setPlaying] = useState(false)
  const isAbout = location.pathname === '/about'

  const noiseLevel = useMemo(() => {
    return isPlayingNoise
      ? masterVolume * NOISE_MULTIPLIER * Number(!isMuted)
      : 0
  }, [masterVolume, isPlayingNoise, isMuted])

  const radioLevel = useMemo(() => {
    return masterVolume * Number(!isMuted)
  }, [masterVolume, isMuted])

  // set volume
  useEffect(() => {
    if (noiseRef.current) {
      noiseVolume.set(noiseLevel)
    }
  }, [noiseLevel])

  useEffect(() => {
    if (audioRef.current) {
      radioVolume.set(radioLevel)
    }
  }, [radioLevel])

  // start play sound
  useEffect(() => {
    if (canPlaySound && permissionsGranted) {
      clearTimeout(timeoutId)
      setPlayingNoise(true)

      if (year && audioRef.current) {
        timeoutId = setTimeout(() => {
          playStart(audioRef, year, addNotification)
          setPlayingNoise(false)
        }, 200)
      }
    }

    return () => clearTimeout(timeoutId)
  }, [canPlaySound, permissionsGranted, year])

  useEffect(() => {
    if (!year && audioRef.current) {
      audioRef.current.currentTime = 0
      audioRef.current.pause()
    }
  }, [year])

  const onPlayButtonHandler = useCallback(() => {
    if (year && audioRef.current) {
      playStart(audioRef, year, addNotification)
    }
  }, [year])

  const onPauseButtonHandler = useCallback(() => {
    if (audioRef.current) {
      setPlaying(false)
      audioRef.current.pause()
    }
  }, [])

  const onSlideHandler = useCallback((value: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value
    }
  }, [])

  useEffect(() => {
    const unsubscribeRadio = radioVolume.onChange(v => {
      if (radioControls.gainNode && radioControls.audioCtx) {
        radioControls.gainNode.gain.value = v
      }
    })
    const unsubscribeNoise = noiseVolume.onChange(v => {
      if (noiseControls.gainNode && noiseControls.audioCtx) {
        noiseControls.gainNode.gain.value = v * NOISE_MULTIPLIER
      }
    })

    return () => {
      unsubscribeRadio()
      unsubscribeNoise()
    }
  }, [])

  useEffect(() => {
    if (isAbout) {
      onPauseButtonHandler()
    }
  }, [isAbout])

  return (
    <>
      <audio
        crossOrigin=""
        src="https://docs.rferl.org/Infographics/2023/2023_01/2023_01_Anniversary/audio/radio_noise.mp3"
        loop
        ref={noiseRef}
      />
      <audio
        crossOrigin=""
        src={audioUrl}
        onTimeUpdate={evt => {
          setCurrentTime(evt.currentTarget.currentTime)
        }}
        onLoadedMetadata={evt => {
          setDuration(evt.currentTarget.duration)
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onAbort={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        preload="auto"
        ref={audioRef}
      />

      <WelcomeModal
        isOpen={!permissionsGranted}
        onClose={() => {
          // Radio
          radioControls.audioCtx = new AudioContext()
          radioControls.gainNode = radioControls.audioCtx.createGain()
          const radioSource = radioControls.audioCtx.createMediaElementSource(
            audioRef.current!
          )
          radioSource.connect(radioControls.gainNode)
          radioControls.gainNode.connect(radioControls.audioCtx.destination)

          // Noise
          noiseControls.audioCtx = new AudioContext()
          noiseControls.gainNode = noiseControls.audioCtx.createGain()
          const noiseSource = noiseControls.audioCtx.createMediaElementSource(
            noiseRef.current!
          )
          noiseSource.connect(noiseControls.gainNode)

          if (!canPlaySound) {
            noiseControls.gainNode.gain.value = 0
          }

          noiseControls.gainNode.connect(noiseControls.audioCtx.destination)

          playStart(audioRef)
          playStart(noiseRef)

          grantPermissions()
        }}
      />

      {!isAbout && (
        <Player
          isHomepage={isHomepage}
          isPlaying={isPlaying}
          isMuted={isMuted}
          year={year}
          currentTime={currentTime}
          duration={duration}
          onSlide={value => onSlideHandler(value)}
          onPlay={onPlayButtonHandler}
          onPause={onPauseButtonHandler}
          onMuteToggle={toggleMuted}
        />
      )}
    </>
  )
}
